import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Stack,
  Tooltip,
  Badge,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  AccessTime as ClockIcon,
  LocationOn as LocationIcon,
  Rocket as RocketIcon,
  Download as DownloadIcon,
  DirectionsCar as CarIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import clusteringService, { ClusteringAlgorithm, DetailedClusterResponse, ClusterRequest, WorkerAssignmentRequest } from '../services/clusteringService';
import Map2GIS from './Map2GIS';

interface PlanClustersViewProps {
  planId: number;
  planName: string;
}

const PlanClustersView: React.FC<PlanClustersViewProps> = ({ planId, planName }) => {
  const API_BASE_URL = 'http://127.0.0.1:8000';
  
  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState<DetailedClusterResponse[]>([]);
  const [algorithms, setAlgorithms] = useState<ClusteringAlgorithm[]>([]);
  const [clusterModalVisible, setClusterModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [optimizeModalVisible, setOptimizeModalVisible] = useState(false);
  const [optimizingCluster, setOptimizingCluster] = useState<number | null>(null);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('');
  const [assignmentStrategy, setAssignmentStrategy] = useState('balanced');
  const [algorithmParams, setAlgorithmParams] = useState<any>({});
  const [optimizeParams, setOptimizeParams] = useState({
    transport_type: 'car',
    time_limit_seconds: 30,
    use_time_windows: true,
    optimize_transport_modes: false,
    allow_mode_change: true,
    prioritize_efficiency: true,
    transport_optimization_iterations: 3
  });

  useEffect(() => {
    loadAlgorithms();
    loadClusters();
  }, [planId]);

  const loadAlgorithms = async () => {
    try {
      await clusteringService.initializeAlgorithms();
      const data = await clusteringService.getAlgorithms();
      setAlgorithms(data);
    } catch (_error) {
      console.error('Failed to load algorithms:', _error);
    }
  };

  const loadClusters = async () => {
    setLoading(true);
    try {
      const response = await clusteringService.getPlanClusters(planId);
      setClusters(response.clusters);
    } catch (_error) {
      console.error('Failed to load clusters:', _error);
      setClusters([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClusters = async () => {
    try {
      const selectedAlgo = algorithms.find(a => a.name === selectedAlgorithm);
      
      const request: ClusterRequest = {
        algorithm: selectedAlgorithm,
        parameters: algorithmParams || selectedAlgo?.default_params || {}
      };

      setLoading(true);
      await clusteringService.clusterPlanStores(planId, request);
      alert('Clusters created successfully');
      setClusterModalVisible(false);
      loadClusters();
    } catch (_error) {
      alert('Failed to create clusters');
      console.error(_error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignWorkers = async () => {
    try {
      const request: WorkerAssignmentRequest = {
        strategy: assignmentStrategy as any
      };

      setLoading(true);
      await clusteringService.assignWorkersToCluster(planId, request);
      alert('Workers assigned successfully');
      setAssignModalVisible(false);
      loadClusters();
    } catch (_error) {
      alert('Failed to assign workers');
      console.error(_error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClusters = async () => {
    if (window.confirm('Are you sure you want to delete all clusters for this plan?')) {
      try {
        setLoading(true);
        await clusteringService.deletePlanClusters(planId);
        alert('Clusters deleted successfully');
        loadClusters();
      } catch (_error) {
        alert('Failed to delete clusters');
        console.error(_error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOptimizeCluster = async (clusterId: number) => {
    setOptimizingCluster(clusterId);
    setOptimizeModalVisible(true);
  };

  const handleOptimizeSubmit = async () => {
    try {
      setLoading(true);
      
      // Submit task to queue instead of running directly
      const response = await fetch(`${API_BASE_URL}/api/v1/task-queue/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_type: 'cluster_optimization',
          plan_id: planId,
          cluster_id: optimizingCluster,  // Pass cluster_id at top level
          priority: 2, // HIGH priority
          clustering_params: {
            use_existing_clusters: true  // Don't create new clusters
          },
          optimization_params: optimizeParams,
          metadata: {
            cluster_id: optimizingCluster,
            plan_name: planName
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit optimization task');
      }
      
      const result = await response.json();
      alert(`Optimization task submitted successfully!\nTask ID: ${result.task_id}\nStatus: ${result.status}\n\nYou can check the task status in the Optimization Results view.`);
      setOptimizeModalVisible(false);
    } catch (_error) {
      alert('Failed to submit optimization task');
      console.error(_error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeAllClusters = async () => {
    if (window.confirm('This will submit optimization tasks for all clusters. Continue?')) {
      try {
        setLoading(true);
        
        // Get current clustering info to pass to the task
        const clusteringInfo = clusters.length > 0 ? {
          algorithm: clusters[0].type || 'unknown',
          cluster_count: clusters.length
        } : {};
        
        // Submit full optimization task to queue
        const response = await fetch(`${API_BASE_URL}/api/v1/task-queue/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            task_type: 'cluster_optimization',
            plan_id: planId,
            priority: 3, // URGENT priority for full optimization
            clustering_params: {
              use_existing_clusters: true,  // Don't create new clusters
              optimize_all: true,
              ...clusteringInfo
            },
            optimization_params: {
              transport_type: 'car',
              time_limit_seconds: 30,
              use_time_windows: true
            },
            metadata: {
              plan_name: planName,
              cluster_count: clusters.length,
              total_stores: clusters.reduce((sum, c) => sum + c.store_count, 0)
            }
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to submit optimization task');
        }
        
        const result = await response.json();
        alert(`Optimization task submitted successfully!\nTask ID: ${result.task_id}\nStatus: ${result.status}\n\nAll ${clusters.length} clusters will be optimized.\nCheck the Optimization Results view for progress.`);
      } catch (_error) {
        alert('Failed to submit optimization task');
        console.error(_error);
      } finally {
        setLoading(false);
      }
    }
  };

  const getClusterTypeColor = (type: string): any => {
    const colors: Record<string, any> = {
      'kmeans': 'primary',
      'hierarchical': 'success',
      'dbscan': 'secondary',
      'supervisor': 'warning',
      'workload': 'error',
      'hybrid': 'info'
    };
    return colors[type] || 'default';
  };

  const renderClusterStats = () => {
    const totalStores = clusters.reduce((sum, c) => sum + c.store_count, 0);
    // const totalVisits = clusters.reduce((sum, c) => sum + c.total_visits, 0);
    const totalHours = clusters.reduce((sum, c) => sum + c.total_duration_hours, 0);
    const totalWorkers = clusters.reduce((sum, c) => sum + c.workers.length, 0);

    return (
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 23%', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <GroupIcon color="primary" />
                <Box>
                  <Typography variant="h6">{clusters.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Clusters</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 23%', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <StoreIcon color="primary" />
                <Box>
                  <Typography variant="h6">{totalStores}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Stores</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 23%', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PersonIcon color="primary" />
                <Box>
                  <Typography variant="h6">{totalWorkers}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Workers</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 23%', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ClockIcon color="primary" />
                <Box>
                  <Typography variant="h6">{totalHours.toFixed(1)}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Hours</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  };

  const renderMap = () => {
    const mapStores = clusters.flatMap(cluster =>
      cluster.stores.map(store => ({
        id: store.id,
        name: store.name,
        address: store.address,
        lat: store.lat || 0,
        lng: store.lng || 0,
        cluster_id: cluster.cluster_id,
        cluster_name: cluster.name,
        cluster_color: getClusterTypeColor(cluster.type)
      }))
    );

    const clusterCenters = clusters
      .filter(c => c.center_lat && c.center_lng)
      .map(cluster => ({
        id: `cluster-${cluster.id}`,
        name: cluster.name || `Cluster ${cluster.cluster_id}`,
        lat: cluster.center_lat!,
        lng: cluster.center_lng!,
        isClusterCenter: true,
        type: cluster.type
      }));

    return (
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Cluster Map" />
        <CardContent>
          <Map2GIS
            stores={[...mapStores, ...clusterCenters]}
            height="500px"
            showClusters={true}
          />
        </CardContent>
      </Card>
    );
  };

  const renderClusterDetails = (cluster: DetailedClusterResponse) => {
    return (
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <LocationIcon />
                {cluster.center_lat && cluster.center_lng ? (
                  <Typography>Center: {cluster.center_lat.toFixed(6)}, {cluster.center_lng.toFixed(6)}</Typography>
                ) : (
                  <Typography>No center coordinates</Typography>
                )}
                {cluster.max_distance_km && (
                  <Chip label={`Max distance: ${cluster.max_distance_km.toFixed(2)} km`} size="small" />
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader title="Stores" titleTypographyProps={{ variant: 'h6' }} />
            <CardContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell align="right">Visits</TableCell>
                      <TableCell align="right">Duration (min)</TableCell>
                      <TableCell align="right">Distance (km)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cluster.stores.map((store) => (
                      <TableRow key={store.id}>
                        <TableCell>{store.name}</TableCell>
                        <TableCell>{store.address}</TableCell>
                        <TableCell align="right">{store.visit_count}</TableCell>
                        <TableCell align="right">{store.duration_minutes}</TableCell>
                        <TableCell align="right">{store.distance_km?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {cluster.workers.length > 0 && (
            <Card variant="outlined">
              <CardHeader title="Workers" titleTypographyProps={{ variant: 'h6' }} />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Max Stores</TableCell>
                        <TableCell align="right">Assigned</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cluster.workers.map((worker: any) => (
                        <TableRow key={worker.id}>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <span>{worker.name}</span>
                              {worker.is_primary && <Chip label="Primary" size="small" color="warning" />}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{worker.max_stores}</TableCell>
                          <TableCell align="right">
                            <Badge badgeContent={worker.assigned_stores} color="primary">
                              <span>stores</span>
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Box>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <GroupIcon />
              <Typography variant="h5">Plan Clusters: {planName}</Typography>
            </Stack>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button 
                onClick={() => loadClusters()}
                sx={{ minWidth: 'auto', p: 1 }}
                color="inherit"
              >
                <RefreshIcon />
              </Button>
              <Button variant="contained" onClick={() => setClusterModalVisible(true)}>
                Create Clusters
              </Button>
              {clusters.length > 0 && (
                <>
                  <Button onClick={() => setAssignModalVisible(true)}>
                    Assign Workers
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<RocketIcon />}
                    onClick={handleOptimizeAllClusters}
                  >
                    Optimize All
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteClusters}
                  >
                    Delete All
                  </Button>
                </>
              )}
            </Stack>
          }
        />
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : clusters.length === 0 ? (
            <Alert severity="info">
              No clusters created yet. Click "Create Clusters" to get started.
            </Alert>
          ) : (
            <>
              {renderClusterStats()}
              {renderMap()}
              
              <Box sx={{ mt: 2 }}>
                {clusters.map((cluster) => (
                  <Accordion key={cluster.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <Chip
                          label={cluster.type}
                          color={getClusterTypeColor(cluster.type)}
                          size="small"
                        />
                        <Typography>{cluster.name || `Cluster ${cluster.cluster_id}`}</Typography>
                        <Badge badgeContent={cluster.store_count} color="primary">
                          <StoreIcon fontSize="small" />
                        </Badge>
                        <Badge badgeContent={cluster.workers.length} color="success">
                          <PersonIcon fontSize="small" />
                        </Badge>
                        <Tooltip title="Total duration">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ClockIcon fontSize="small" />
                            <Typography variant="body2">{cluster.total_duration_hours.toFixed(1)}h</Typography>
                          </Stack>
                        </Tooltip>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<RocketIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOptimizeCluster(cluster.id);
                          }}
                        >
                          Optimize
                        </Button>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {renderClusterDetails(cluster)}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Clusters Dialog */}
      <Dialog open={clusterModalVisible} onClose={() => setClusterModalVisible(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Clusters</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Clustering Algorithm</InputLabel>
              <Select
                value={selectedAlgorithm}
                onChange={(e) => {
                  setSelectedAlgorithm(e.target.value);
                  const algo = algorithms.find(a => a.name === e.target.value);
                  setAlgorithmParams(algo?.default_params || {});
                }}
                label="Clustering Algorithm"
              >
                {algorithms.map((algo) => (
                  <MenuItem key={algo.name} value={algo.name}>
                    <Box>
                      <Typography>{algo.display_name}</Typography>
                      {algo.description && (
                        <Typography variant="caption" color="text.secondary">
                          {algo.description}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedAlgorithm && algorithmParams && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Algorithm Parameters</Typography>
                {Object.entries(algorithmParams).map(([key, value]) => (
                  <TextField
                    key={key}
                    fullWidth
                    label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    value={algorithmParams[key]}
                    onChange={(e) => setAlgorithmParams({
                      ...algorithmParams,
                      [key]: isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)
                    })}
                    margin="normal"
                    type={typeof value === 'number' ? 'number' : 'text'}
                    helperText={key === 'max_distance_km' ? 'Maximum distance from cluster center in kilometers' : ''}
                  />
                ))}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClusterModalVisible(false)}>Cancel</Button>
          <Button onClick={handleCreateClusters} variant="contained">Create Clusters</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Workers Dialog */}
      <Dialog open={assignModalVisible} onClose={() => setAssignModalVisible(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Workers to Clusters</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Assignment Strategy</InputLabel>
            <Select
              value={assignmentStrategy}
              onChange={(e) => setAssignmentStrategy(e.target.value)}
              label="Assignment Strategy"
            >
              <MenuItem value="balanced">
                <Box>
                  <Typography>Balanced</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Distribute workers evenly based on workload
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="supervisor">
                <Box>
                  <Typography>Supervisor-based</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Assign workers based on supervisor relationships
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="proximity">
                <Box>
                  <Typography>Proximity-based</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Assign workers to nearest clusters
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignModalVisible(false)}>Cancel</Button>
          <Button onClick={handleAssignWorkers} variant="contained">Assign Workers</Button>
        </DialogActions>
      </Dialog>

      {/* Optimize Cluster Dialog */}
      <Dialog open={optimizeModalVisible} onClose={() => setOptimizeModalVisible(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Optimize Cluster</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Transport Type</InputLabel>
              <Select
                value={optimizeParams.transport_type}
                onChange={(e) => setOptimizeParams({ ...optimizeParams, transport_type: e.target.value })}
                label="Transport Type"
              >
                <MenuItem value="car">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CarIcon />
                    <span>Car</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="public_transport">Public Transport</MenuItem>
                <MenuItem value="walk">Walking</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Optimization Time Limit (seconds)"
              type="number"
              value={optimizeParams.time_limit_seconds}
              onChange={(e) => setOptimizeParams({ ...optimizeParams, time_limit_seconds: Number(e.target.value) })}
              inputProps={{ min: 10, max: 300 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={optimizeParams.use_time_windows}
                  onChange={(e) => setOptimizeParams({ ...optimizeParams, use_time_windows: e.target.checked })}
                />
              }
              label="Use Store Time Windows"
            />

            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={optimizeParams.optimize_transport_modes}
                  onChange={(e) => setOptimizeParams({ ...optimizeParams, optimize_transport_modes: e.target.checked })}
                />
              }
              label="Optimize Transport Mode Assignments"
            />
            
            {optimizeParams.optimize_transport_modes && (
              <Box sx={{ ml: 3, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={optimizeParams.allow_mode_change}
                      onChange={(e) => setOptimizeParams({ ...optimizeParams, allow_mode_change: e.target.checked })}
                    />
                  }
                  label="Allow Changing Worker Transport Modes"
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={optimizeParams.prioritize_efficiency}
                      onChange={(e) => setOptimizeParams({ ...optimizeParams, prioritize_efficiency: e.target.checked })}
                    />
                  }
                  label="Prioritize Efficiency Over Preferences"
                />
                
                <TextField
                  fullWidth
                  label="Transport Optimization Iterations"
                  type="number"
                  value={optimizeParams.transport_optimization_iterations}
                  onChange={(e) => setOptimizeParams({ ...optimizeParams, transport_optimization_iterations: Number(e.target.value) })}
                  inputProps={{ min: 1, max: 10 }}
                  sx={{ mt: 1 }}
                />
                
                <Alert severity="info" sx={{ mt: 1 }}>
                  Transport optimization will analyze distances and reassign transport modes (auto, public, pedestrian) 
                  to workers for better efficiency. Auto is preferred for long distances, public transport for medium, 
                  and pedestrian for short distances.
                </Alert>
              </Box>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Alert severity="info" icon={<DownloadIcon />}>
              Optimization will be processed by background workers. You can track progress in the Tasks view.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptimizeModalVisible(false)}>Cancel</Button>
          <Button onClick={handleOptimizeSubmit} variant="contained" startIcon={<RocketIcon />}>
            Optimize
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanClustersView;